// SEO / GEO / Social helper functions for supplier public pages

export interface SupplierSeoData {
  company_name: string;
  description: string | null;
  region: string | null;
  slug: string | null;
  custom_domain: string | null;
  ico: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  page_title: string | null;
  meta_description: string | null;
  og_image_url: string | null;
  business_type: string | null;
  service_area: string | null;
  public_tagline: string | null;
  bot_faq: string | null;
}

export function generateTitle(s: SupplierSeoData): string {
  if (s.page_title) return s.page_title;
  const parts = [s.company_name];
  if (s.business_type) parts.push(s.business_type);
  return parts.join(" – ") + " | AI ceník";
}

export function generateDescription(s: SupplierSeoData): string {
  if (s.meta_description) return s.meta_description;
  let desc = `AI ceník od ${s.company_name}`;
  if (s.business_type) desc += ` – ${s.business_type.toLowerCase()}`;
  if (s.service_area || s.region) desc += ` v regionu ${s.service_area || s.region}`;
  desc += ". Získejte orientační cenu online.";
  return desc;
}

export function generateOgImageUrl(s: SupplierSeoData): string {
  return s.og_image_url || "https://cenik.ai/og-image.png";
}

export function generateCanonicalUrl(
  s: SupplierSeoData,
  isCustomDomain: boolean,
  hostname?: string
): string {
  if (isCustomDomain && hostname) {
    return `https://${hostname}/`;
  }
  return `https://cenik.ai/${s.slug || ""}`;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export function parseFaq(text: string | null): FaqItem[] {
  if (!text) return [];
  const items: FaqItem[] = [];
  // Split by "Otázka:" or "Otazka:" markers (case insensitive)
  const blocks = text.split(/(?:otázka|otazka)\s*:\s*/i).filter(Boolean);
  for (const block of blocks) {
    const parts = block.split(/(?:odpověď|odpoved)\s*:\s*/i);
    if (parts.length >= 2) {
      const question = parts[0].trim();
      const answer = parts.slice(1).join("").trim();
      if (question && answer) {
        items.push({ question, answer });
      }
    }
  }
  return items;
}

export function generateJsonLd(
  s: SupplierSeoData,
  canonicalUrl: string
): Record<string, unknown> {
  const ld: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: s.company_name,
    url: canonicalUrl,
  };

  const desc = s.meta_description || s.description;
  if (desc) ld.description = desc;

  const img = generateOgImageUrl(s);
  if (img) ld.image = img;

  if (s.contact_phone) ld.telephone = s.contact_phone;
  if (s.contact_email) ld.email = s.contact_email;
  if (s.service_area || s.region) ld.areaServed = s.service_area || s.region;
  if (s.business_type) ld.knowsAbout = s.business_type;

  if (s.ico) {
    ld.identifier = {
      "@type": "PropertyValue",
      name: "IČO",
      value: s.ico,
    };
  }

  return ld;
}
